import { useMemo, useState } from "react";
import type { WaTemplate } from "../lib/types";
import { ErrorText } from "./ui";

export const languageNames: Record<string, string> = { en_US: "English (US)", en_GB: "English (UK)", en: "English", ar: "Arabic" };
export const categoryNames: Record<string, string> = { UTILITY: "Utility", MARKETING: "Marketing", AUTHENTICATION: "Authentication" };

/** Fills {{n}} variables; unfilled ones stay visible as {{n}}. */
export function fillTemplate(text: string, values: string[]): string {
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (v, n) => values[Number(n) - 1]?.trim() || v);
}

/** TemplateBubble previews a template the way WhatsApp shows it. */
export function TemplateBubble({ header, body, footer, buttons }: { header?: string | null; body: string; footer?: string | null; buttons?: { text: string }[] }) {
  const rtl = /[؀-ۿ]/.test(body);
  return (
    <div className="wa-preview">
      <div className="wa-bubble" dir={rtl ? "rtl" : "auto"}>
        {header && <b className="wa-header">{header}</b>}
        <p>{body || <span className="muted">Your message appears here.</span>}</p>
        {footer && <small className="muted">{footer}</small>}
      </div>
      {buttons && buttons.length > 0 && (
        <div className="wa-buttons">{buttons.map((b, i) => <span key={i}>{b.text}</span>)}</div>
      )}
    </div>
  );
}

/**
 * TemplateSender lets staff pick an approved template, fill its values and
 * send it. The first value is prefilled with the customer's first name.
 */
export function TemplateSender({ templates, firstName, onSend, onCancel }: {
  templates: WaTemplate[];
  firstName?: string | null;
  onSend: (templateId: string, params: string[]) => Promise<void>;
  onCancel?: () => void;
}) {
  const [id, setId] = useState(templates[0]?.id ?? "");
  const [values, setValues] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // Templates may load after this mounts: fall back to the first one.
  const tpl = templates.find((t) => t.id === id) ?? templates[0];
  const vals = useMemo(() => {
    if (!tpl) return [];
    const v = values[tpl.id] ?? Array.from({ length: tpl.variable_count }, (_, i) => (i === 0 && firstName ? firstName : ""));
    return v;
  }, [tpl, values, firstName]);
  if (templates.length === 0) {
    return <p className="muted small">No approved templates yet. Create one under Channels → Message templates; Meta usually reviews them within minutes.</p>;
  }
  const ready = !!tpl && vals.every((v) => v.trim() !== "");
  return (
    <div className="template-sender">
      <label className="field">
        <span>Template</span>
        <select value={tpl?.id ?? ""} onChange={(e) => { setId(e.target.value); setError(null); }}>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>{t.name} · {categoryNames[t.category]} · {languageNames[t.language] ?? t.language}</option>
          ))}
        </select>
      </label>
      {tpl && (
        <div className="template-fill">
          <div>
            {vals.map((v, i) => (
              <label key={i} className="field">
                <span>{`{{${i + 1}}}`}{tpl.examples[i] ? ` (e.g. ${tpl.examples[i]})` : ""}</span>
                <input value={v} maxLength={1000} onChange={(e) => setValues({ ...values, [tpl.id]: vals.map((x, j) => (j === i ? e.target.value : x)) })} />
              </label>
            ))}
            {tpl.category === "MARKETING" && <p className="muted small">Marketing templates need the customer's WhatsApp marketing opt-in on their contact page.</p>}
          </div>
          <TemplateBubble header={tpl.header_text} body={fillTemplate(tpl.body_text, vals)} footer={tpl.footer_text} buttons={tpl.buttons} />
        </div>
      )}
      <ErrorText error={error} />
      <div className="actions">
        <button disabled={!ready || busy} onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await onSend(tpl!.id, vals.map((v) => v.trim()));
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}>{busy ? "Sending…" : "Send template"}</button>
        {onCancel && <button className="secondary" onClick={onCancel}>Cancel</button>}
      </div>
    </div>
  );
}
